// SPDX-License-Identifier: LicenseRef-Pachamama-Proprietary
// Copyright 2026 Pachamama Studios B.V. All rights reserved.

/**
 * AYNI — a period as the baseline: a climatology, read as playback passes
 * the frames (`docs/AYNI_XR_RESEARCH_TIER_PLAN.md`, statistics S2; owner,
 * 2026-10-06; reworked after an independent review the same day).
 *
 * What it computes, per cell of the dataset's own grid:
 *
 * - **The mean of the period**, by calendar month for monthly data (each
 *   frame against the mean of its own month, the WMO convention for
 *   climatological normals: WMO-No. 1203, *WMO Guidelines on the Calculation
 *   of Climate Normals*, 2017), over the whole period for annual data, by
 *   calendar month for daily data, and by calendar month and UTC hour for
 *   data a few hours apart (so the daily cycle is not read as change). A
 *   real-time window shorter than two years, or data more than eight frames
 *   a day, is compared with the mean of the whole window, without σ.
 * - **σ**, the standard deviation of the values of that group about the
 *   period's own linear trend (after Hawkins & Sutton 2012, *Time of
 *   emergence of climate signals*, GRL 39, L01702, here with a linear trend
 *   over the period), on n − 2 degrees of freedom. For monthly and annual
 *   data that is the year-to-year spread; for daily and sub-daily data it is
 *   the spread of that month's (or hour's) values across the period, day to
 *   day and year to year. Never below one quantisation step of the data
 *   (the floored cells are counted); it includes the 8-bit rounding and the
 *   video's compression noise. The standardised change is (value − mean) / σ.
 *
 * Which month (and year) a frame belongs to comes from its **index** on the
 * axis, not from the calendar date of `start + i × cadence`: a cadence of a
 * mean month (or 30 days in a 360-day model calendar) drifts across month
 * boundaries, so dates would put two Januaries in one month and none in
 * February (`calendarFor`, `frameOf`).
 *
 * The running sums are Welford/West's incremental means and co-moments in
 * float32 (Welford 1962; West 1979), so precision does not depend on how far
 * a cell's values sit from any shared reference; tests compare them with a
 * float64 two-pass computation.
 *
 * Honest limits, stated where they are made:
 *
 * - A cell has a mean only where it has data in at least 80 % of the
 *   period's frames of that group, counting the frames the publisher marks
 *   as filled, interpolated, estimated or missing as missing (WMO-No. 1203's
 *   completeness guideline; its rule on consecutive missing years is not
 *   applied); a σ only with at least {@link MIN_SIGMA_YEARS} values in the
 *   cell and as many years in the period.
 * - Memory is capped ({@link CLIMATOLOGY_BUDGET_BYTES}): a count and five
 *   float32 numbers per cell per group. Above the cap the cells are taken
 *   every 2nd (or 3rd …) texel in each direction, and the frame on screen is
 *   read at the same texel, so the change is drawn at that resolution.
 *
 * The finished climatology is held as 16-bit numbers per cell per group:
 * the mean as a fractional code of the data's own value table (8.8 fixed
 * point, decoded by interpolating the table, so a log scale keeps its
 * relative precision) and σ as a multiple of the data's local step (8.8).
 * The graphics card draws from those, and the paused picture, the pointer,
 * the pins and the statistics read the same numbers.
 *
 * Pure: no DOM.
 */

import type { ChangeMode } from './changeMap'
import type { LumaSnapshot } from '../glLumaSampler'
import type { DatasetOverlayOptions } from '../../types'
import { texelUvToLatLon } from '../datasetProbe'
import { inWindowMask, rowAreasKm2, type TexelWindow } from '../datasetStats'
import { createFrameAxis, frameTimeMs, nearestFrameAtTimeMs, type FrameAxis } from '../frameCalendar'

const DAY_MS = 86_400_000
const HOUR_MS = 3_600_000
/** The mean Gregorian year. */
export const YEAR_MS = 365.2425 * DAY_MS
/** The mean Gregorian month. */
export const MONTH_MS = YEAR_MS / 12

/**
 * How frames are grouped: by calendar month (monthly, or daily data), one
 * group for annual data, by calendar month and UTC hour (sub-daily), or
 * one for a window.
 */
export type PeriodGrouping = 'monthly' | 'annual' | 'daily' | 'subdaily' | 'window'

/** A dataset's time axis: frame i stands at `startMs + i × cadenceMs` (a stream's .dsa timeline). */
export interface ClimateAxis {
  readonly startMs: number
  readonly frameCount: number
  readonly cadenceMs: number
}

/** A period of whole calendar years, both ends included (UTC). */
export interface ClimatePeriod {
  readonly from: number
  readonly to: number
}

/** The WMO's current standard normal period. */
export const STANDARD_PERIOD: ClimatePeriod = { from: 1991, to: 2020 }
/** Years in a WMO climatological normal. */
export const NORMAL_YEARS = 30
/** Values (and years) below which σ is not estimated (the statistics' own minimum, S1). */
export const MIN_SIGMA_YEARS = 10
/** Share of a group's frames a cell needs for a mean (WMO-No. 1203: 80 %). */
export const MIN_COVERAGE = 0.8
/** The memory the running sums may take: 256 MiB. */
export const CLIMATOLOGY_BUDGET_BYTES = 256 * 1024 * 1024
/** The σ change's fixed colour range: ±3σ spans the palette. */
export const SIGMA_LIMIT = 3
/** 16-bit code for "no mean here". */
export const NO_MEAN = 65535
/** Sub-daily data is grouped by UTC hour up to this many frames a day (12 × 8 = 96 groups, one texture layer each). */
export const MAX_SLOTS_PER_DAY = 8
/** The most frames one cell can count in a group (a 16-bit count). */
export const MAX_PER_GROUP = 65535

/** How an axis's frames map to groups and years. */
export interface ClimateCalendar {
  readonly grouping: PeriodGrouping
  /** Groups (texture layers): 12 by month, 12 × slots by month and hour, or 1. */
  readonly groups: number
  /** Monthly or annual data: months per frame (1, 2, 3, 4, 6 or 12), the first frame's year and month. */
  readonly monthsPerFrame: number
  readonly y0: number
  readonly m0: number
  /** Sub-daily data: hour slots per day and hours per slot. */
  readonly slotsPerDay: number
  readonly slotHours: number
  /** Why a window: shorter than two years, or more than {@link MAX_SLOTS_PER_DAY} frames a day. */
  readonly windowReason: 'short' | 'tooFine' | null
}

/**
 * How frames are grouped, from the cadence:
 *
 * - within 6 % of k mean months (k = 1, 2, 3, 4, 6): by calendar month,
 *   counted from the first frame's stamp (index-based, no date drift: a
 *   stamp on the 1st, the 3rd or the 16th all work);
 * - within 6 % of a year (365, 360 or 365.2425 days): annual;
 * - a day or more otherwise (daily, 5- or 8-day composites): by the calendar
 *   month of each frame's middle (real dates do not drift);
 * - shorter: by calendar month and UTC hour when at most eight frames a day,
 *   else a window;
 * - an axis under two years, or without a cadence: a window.
 */
export function calendarFor(axis: ClimateAxis): ClimateCalendar {
  const base = { monthsPerFrame: 0, y0: 0, m0: 0, slotsPerDay: 0, slotHours: 0 }
  const window = (reason: 'short' | 'tooFine'): ClimateCalendar => ({ ...base, grouping: 'window', groups: 1, windowReason: reason })
  const c = axis.cadenceMs
  if (!(c > 0) || !Number.isFinite(axis.startMs) || axis.frameCount < 2) return window('short')
  if (axis.frameCount * c < 2 * YEAR_MS) return window('short')
  for (const k of [1, 2, 3, 4, 6, 12]) {
    if (Math.abs(c - k * MONTH_MS) <= 0.06 * k * MONTH_MS) {
      // The first frame's own stamp, as its time label shows it; later frames by index.
      const first = new Date(axis.startMs)
      return {
        ...base,
        grouping: k === 12 ? 'annual' : 'monthly',
        groups: k === 12 ? 1 : 12,
        monthsPerFrame: k,
        y0: first.getUTCFullYear(),
        m0: k === 12 ? 0 : first.getUTCMonth(),
        windowReason: null,
      }
    }
  }
  if (c >= DAY_MS * 0.999) return { ...base, grouping: 'daily', groups: 12, windowReason: null }
  const slotsPerDay = Math.ceil(DAY_MS / c - 1e-6)
  if (slotsPerDay > MAX_SLOTS_PER_DAY) return window('tooFine')
  return { ...base, grouping: 'subdaily', groups: 12 * slotsPerDay, slotsPerDay, slotHours: c / HOUR_MS, windowReason: null }
}

/** Where a frame belongs: its group, year, and its time in fractional years (for the trend). */
export interface FrameCalendar {
  readonly group: number
  readonly year: number
  /** Fractional calendar year of the frame (2001.04 for January 2001). */
  readonly yearFrac: number
}

/** The group, year and time of frame i (one function for the reader, the globe and the pins). */
export function frameOf(axis: ClimateAxis, cal: ClimateCalendar, i: number): FrameCalendar {
  if (cal.grouping === 'monthly' || cal.grouping === 'annual') {
    const mi = cal.m0 + cal.monthsPerFrame * i
    const year = cal.y0 + Math.floor(mi / 12)
    const month = ((mi % 12) + 12) % 12
    return { group: cal.grouping === 'annual' ? 0 : month, year, yearFrac: year + (month + cal.monthsPerFrame / 2) / 12 }
  }
  const stamp = axis.startMs + i * axis.cadenceMs
  // Daily and coarser: the frame's middle; finer: its stamp (the hour it is of).
  const ms = cal.grouping === 'daily' ? stamp + axis.cadenceMs / 2 : stamp
  const d = new Date(ms)
  const year = d.getUTCFullYear()
  const yearFrac = year + (ms - Date.UTC(year, 0, 1)) / (Date.UTC(year + 1, 0, 1) - Date.UTC(year, 0, 1))
  if (cal.grouping === 'daily') return { group: d.getUTCMonth(), year, yearFrac }
  if (cal.grouping === 'subdaily') {
    const hour = (((ms % DAY_MS) + DAY_MS) % DAY_MS) / HOUR_MS
    const slot = Math.min(cal.slotsPerDay - 1, Math.floor(hour / cal.slotHours + 1e-9))
    return { group: d.getUTCMonth() * cal.slotsPerDay + slot, year, yearFrac }
  }
  return { group: 0, year, yearFrac }
}

/** The axis as the app's frame calendar reads it (`frameCalendar.ts`), built once per axis seen last. */
let lastAxis: { key: string; axis: FrameAxis } | null = null
function calendarAxis(axis: ClimateAxis): FrameAxis {
  const key = `${axis.startMs}|${axis.frameCount}|${axis.cadenceMs}`
  if (lastAxis?.key !== key) lastAxis = { key, axis: createFrameAxis(axis) }
  return lastAxis.axis
}

/**
 * Frame i's stamp as the app labels it (the date track, the readout, the
 * pins' times): on a monthly or yearly stream the calendar's month or year
 * from the first stamp (`frameCalendar.ts`), on a finer one `startMs + i ×
 * cadence`. The grouping above keeps its own index arithmetic; a test
 * holds the two to the same calendar month on monthly and yearly axes.
 */
export function climateFrameTimeMs(axis: ClimateAxis, i: number): number {
  return frameTimeMs(calendarAxis(axis), i)
}

/**
 * The frame an instant falls on (nearest stamp, clamped): how a pin's slot
 * finds its frame. The inverse of {@link climateFrameTimeMs}: the times it
 * is given are the app's frame stamps, which on a monthly axis are calendar
 * months, not multiples of the declared cadence (a 30-day cadence would
 * drift by a frame every 34 months).
 */
export function frameAtTime(axis: ClimateAxis, ms: number): number {
  if (!Number.isFinite(ms) || !(axis.cadenceMs > 0)) return 0
  return nearestFrameAtTimeMs(calendarAxis(axis), ms)
}

/** The calendar month and, for sub-daily data, the UTC hours of a group. */
export function groupParts(cal: ClimateCalendar, group: number): { month: number | null; fromHour: number | null; toHour: number | null } {
  if (cal.grouping === 'monthly' || cal.grouping === 'daily') return { month: group, fromHour: null, toHour: null }
  if (cal.grouping === 'subdaily') {
    const slot = group % cal.slotsPerDay
    return { month: Math.floor(group / cal.slotsPerDay), fromHour: slot * cal.slotHours, toHour: Math.min(24, (slot + 1) * cal.slotHours) }
  }
  return { month: null, fromHour: null, toHour: null }
}

const MONTH_NAMES = ['January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December']

/**
 * A group in English, for the CSV, the figure and the methods paragraph
 * ("January", "January, 03:00–06:00 UTC"); null for annual data or a window.
 */
export function groupLabel(cal: ClimateCalendar, group: number): string | null {
  const parts = groupParts(cal, group)
  if (parts.month === null || !(parts.month >= 0 && parts.month < 12)) return null
  const month = MONTH_NAMES[parts.month]
  if (parts.fromHour === null || parts.toHour === null) return month
  const hh = (h: number) => `${String(Math.floor(h)).padStart(2, '0')}:${String(Math.round((h % 1) * 60)).padStart(2, '0')}`
  return `${month}, ${hh(parts.fromHour)}–${hh(parts.toHour)} UTC`
}

/** What σ is for a grouping, in English (CSV rows): the spread it measures, how, and its limits. */
export function sigmaWordsText(grouping: PeriodGrouping): string {
  if (grouping === 'window') return 'no sigma (a window has no year-to-year spread)'
  const spread = grouping === 'monthly' || grouping === 'annual'
    ? 'year-to-year standard deviation'
    : grouping === 'daily'
      ? "standard deviation of that month's daily values across the period (day-to-day and year-to-year)"
      : 'standard deviation of the values of that month and UTC hour across the period (day-to-day and year-to-year)'
  return `sigma: ${spread} about the period's linear trend, n - 2 degrees of freedom (after Hawkins & Sutton 2012, here with a linear trend over the period); never below one quantisation step at the mean; includes the data's rounding and streaming noise; none under ${MIN_SIGMA_YEARS} values in a cell or ${MIN_SIGMA_YEARS} years in the period`
}

/** The climatology's method in English, one line for the CSVs (statistics, latitude, area over a threshold). */
export function periodMethodText(grouping: PeriodGrouping): string {
  const mean = grouping === 'monthly'
    ? 'mean of each calendar month over the period (WMO climatological normal; WMO-No. 1203, 2017); each frame against its own month, the month taken from the frame index'
    : grouping === 'annual'
      ? 'mean of the annual values over the period'
      : grouping === 'daily'
        ? "mean of each calendar month's daily values over the period; each frame against its own month"
        : grouping === 'subdaily'
          ? 'mean of each calendar month at each UTC hour slot over the period; each frame against its own month and hours'
          : 'mean of every frame in the window'
  return `${mean}; a cell needs data in at least 80 % of the frames of its group, frames the publisher filled, interpolated, estimated or lists as missing counting as missing (WMO's 80 % guideline; its rule on consecutive missing years is not applied); ${sigmaWordsText(grouping)}; mean kept as a 16-bit fractional code of the data's value table, sigma as a 16-bit multiple of the step at the mean`
}

/** The groups the axis can visit (seasonal data visits 4 months of 12). */
export function expectedGroups(cal: ClimateCalendar): number[] {
  if (cal.grouping === 'monthly') {
    const out = new Set<number>()
    for (let j = 0; j < 12; j++) out.add((cal.m0 + cal.monthsPerFrame * j) % 12)
    return [...out].sort((a, b) => a - b)
  }
  return Array.from({ length: cal.groups }, (_, g) => g)
}

/** The UTC years of the axis's first and last frame. */
export function axisYears(axis: ClimateAxis): ClimatePeriod {
  const cal = calendarFor(axis)
  return { from: frameOf(axis, cal, 0).year, to: frameOf(axis, cal, Math.max(0, axis.frameCount - 1)).year }
}

/**
 * The period offered first: 1991–2020 clipped to the axis (the WMO's
 * standard normal, so a result is comparable with published ones). When
 * the axis holds under {@link MIN_SIGMA_YEARS} of it (a projection that
 * starts in 2015), the 30 years nearest 1991–2020; an axis shorter than 30
 * years, all of it. Null for a window: it has no years to choose.
 */
export function defaultPeriod(axis: ClimateAxis): ClimatePeriod | null {
  if (calendarFor(axis).grouping === 'window') return null
  const years = axisYears(axis)
  const from = Math.max(STANDARD_PERIOD.from, years.from)
  const to = Math.min(STANDARD_PERIOD.to, years.to)
  if (to - from + 1 >= MIN_SIGMA_YEARS) return { from, to }
  if (years.to - years.from + 1 >= NORMAL_YEARS) {
    return years.from > STANDARD_PERIOD.from
      ? { from: years.from, to: years.from + NORMAL_YEARS - 1 }
      : { from: years.to - NORMAL_YEARS + 1, to: years.to }
  }
  return years
}

/** A period the reader typed, kept inside the axis and in order; null when it misses the axis. */
export function clampPeriod(axis: ClimateAxis, period: ClimatePeriod): ClimatePeriod | null {
  const years = axisYears(axis)
  const from = Math.max(years.from, Math.round(Math.min(period.from, period.to)))
  const to = Math.min(years.to, Math.round(Math.max(period.from, period.to)))
  return Number.isFinite(from) && Number.isFinite(to) && from <= to ? { from, to } : null
}

/** One frame of the period: where it sits on the axis, its group and year, its time from the period's centre in years. */
export interface PeriodFrame {
  readonly frame: number
  readonly group: number
  readonly year: number
  readonly t: number
  /** False for a frame the publisher filled, interpolated, estimated or lists as missing: not read, counted as missing. */
  readonly usable: boolean
}

/** The frames a period holds (a window: every frame); `usable(i)` says whether the publisher measured frame i. */
export function periodFrames(
  axis: ClimateAxis,
  cal: ClimateCalendar,
  period: ClimatePeriod | null,
  usable: (frame: number) => boolean = () => true,
): PeriodFrame[] {
  const inside: Array<{ frame: number; f: FrameCalendar }> = []
  for (let i = 0; i < axis.frameCount; i++) {
    const f = frameOf(axis, cal, i)
    if (cal.grouping !== 'window' && period && (f.year < period.from || f.year > period.to)) continue
    inside.push({ frame: i, f })
  }
  if (!inside.length) return []
  // Time is centred on the period, so the trend's moments stay small.
  const mid = cal.grouping !== 'window' && period
    ? (period.from + period.to + 1) / 2
    : (inside[0].f.yearFrac + inside[inside.length - 1].f.yearFrac) / 2
  return inside.map(({ frame, f }) => ({ frame, group: f.group, year: f.year, t: f.yearFrac - mid, usable: usable(frame) }))
}

/** Whether a period can be a baseline, and what σ it allows. */
export interface PeriodCheck {
  /** Frames in the period that can be read (the publisher measured them). */
  readonly frames: number
  /** Frames the publisher filled, interpolated, estimated or lists as missing. */
  readonly skipped: number
  /** Distinct years with a readable frame, per group. */
  readonly yearsPerGroup: readonly number[]
  /** Readable frames per group. */
  readonly framesPerGroup: readonly number[]
  /** All frames per group, skipped ones too: the 80 % rule counts against these. */
  readonly expectedPerGroup: readonly number[]
  /** The fewest years in a group the axis visits. */
  readonly minYears: number
  /** Why it cannot be a baseline: no frames; a group the axis visits with under two frames; a count past 16 bits. */
  readonly refusal: 'empty' | 'tooFew' | 'tooMany' | null
  /** σ: available; on fewer than 30 years; too few years; or none (a window). */
  readonly sigma: 'ok' | 'short' | 'tooShort' | 'window'
}

export function checkPeriod(frames: readonly PeriodFrame[], cal: ClimateCalendar): PeriodCheck {
  const framesPerGroup = new Array<number>(cal.groups).fill(0)
  const expectedPerGroup = new Array<number>(cal.groups).fill(0)
  const years = Array.from({ length: cal.groups }, () => new Set<number>())
  let usable = 0
  for (const f of frames) {
    expectedPerGroup[f.group]++
    if (!f.usable) continue
    usable++
    framesPerGroup[f.group]++
    years[f.group].add(f.year)
  }
  const yearsPerGroup = years.map((s) => s.size)
  const visited = expectedGroups(cal)
  const minYears = visited.length ? Math.min(...visited.map((g) => yearsPerGroup[g])) : 0
  const refusal = usable === 0
    ? 'empty'
    : visited.some((g) => framesPerGroup[g] < 2)
      ? 'tooFew'
      : framesPerGroup.some((n) => n > MAX_PER_GROUP)
        ? 'tooMany'
        : null
  const sigma = cal.grouping === 'window' ? 'window' : minYears < MIN_SIGMA_YEARS ? 'tooShort' : minYears < NORMAL_YEARS ? 'short' : 'ok'
  return { frames: usable, skipped: frames.length - usable, yearsPerGroup, framesPerGroup, expectedPerGroup, minYears, refusal, sigma }
}

// ---------------------------------------------------------------- running sums

/**
 * Bytes per cell per group: a count, then five numbers (two means, three
 * co-moments) in float32, except the value's mean in float64 once a group
 * can hold more than 255 frames (daily and finer): past a few thousand
 * frames a float32 mean can no longer take its own increments (300 K plus
 * 1/22,320 of a kelvin rounds away).
 */
export function bytesPerCell(maxPerGroup: number): number {
  return maxPerGroup <= 255 ? 1 + 5 * 4 : 2 + 4 * 4 + 8
}

/** The cells the sums are kept on: every `factor`-th texel each way, the smallest factor within the budget. */
export function planGrid(
  width: number,
  height: number,
  groups: number,
  maxPerGroup: number,
  budget: number = CLIMATOLOGY_BUDGET_BYTES,
): { factor: number; cw: number; ch: number; bytes: number } {
  const per = bytesPerCell(maxPerGroup) * groups
  for (let factor = 1; ; factor++) {
    const cw = Math.ceil(width / factor)
    const ch = Math.ceil(height / factor)
    const bytes = cw * ch * per
    if (bytes <= budget || (cw === 1 && ch === 1)) return { factor, cw, ch, bytes }
  }
}

/**
 * The running sums, Welford/West style: per cell and group the count, the
 * means of t and of the value, and the co-moments Σ(t−t̄)², Σ(v−v̄)²,
 * Σ(t−t̄)(v−v̄). Each update is computed in double precision and stored in
 * float32 (the value's mean in float64 for groups of more than 255 frames).
 */
export interface ClimatologyAccumulator {
  /** The dataset's grid (the map rectangle). */
  readonly width: number
  readonly height: number
  /** Cells are every `factor`-th texel each way; 1 is every texel. */
  readonly factor: number
  readonly cw: number
  readonly ch: number
  readonly groups: number
  readonly n: Uint8Array | Uint16Array
  readonly mt: Float32Array
  readonly mv: Float32Array | Float64Array
  readonly stt: Float32Array
  readonly svv: Float32Array
  readonly stv: Float32Array
  /** Frames read into each group, and their distinct years. */
  readonly framesPerGroup: number[]
  readonly yearsPerGroup: Array<Set<number>>
}

export function createAccumulator(
  width: number,
  height: number,
  groups: number,
  maxPerGroup: number,
  budget: number = CLIMATOLOGY_BUDGET_BYTES,
): ClimatologyAccumulator {
  const { factor, cw, ch } = planGrid(width, height, groups, maxPerGroup, budget)
  const size = cw * ch * groups
  return {
    width,
    height,
    factor,
    cw,
    ch,
    groups,
    n: maxPerGroup <= 255 ? new Uint8Array(size) : new Uint16Array(size),
    mt: new Float32Array(size),
    mv: maxPerGroup <= 255 ? new Float32Array(size) : new Float64Array(size),
    stt: new Float32Array(size),
    svv: new Float32Array(size),
    stv: new Float32Array(size),
    framesPerGroup: new Array<number>(groups).fill(0),
    yearsPerGroup: Array.from({ length: groups }, () => new Set<number>()),
  }
}

/** The texel a cell samples: the one at its block's centre (clamped to the grid). */
export function cellTexel(factor: number, cx: number, cy: number, width: number, height: number): { x: number; y: number } {
  const half = factor >> 1
  return { x: Math.min(width - 1, cx * factor + half), y: Math.min(height - 1, cy * factor + half) }
}

/** The texel a cell samples, for the texel (x, y) of the grid: identity when the grid is not thinned. */
export function sampledTexel(clim: { factor: number; width: number; height: number }, x: number, y: number): { x: number; y: number } {
  if (clim.factor === 1) return { x, y }
  return cellTexel(clim.factor, Math.floor(x / clim.factor), Math.floor(y / clim.factor), clim.width, clim.height)
}

/**
 * Add one frame's codes to its group's sums. `values` turns a code into its
 * value (NaN for no data: `changeMap.valueTable`). False when the frame is
 * on another grid, or a cell's count is full.
 */
export function accumulateFrame(
  acc: ClimatologyAccumulator,
  codes: Uint8Array,
  frameWidth: number,
  frameHeight: number,
  values: ArrayLike<number>,
  frame: Pick<PeriodFrame, 'group' | 'year' | 't'>,
): boolean {
  if (frameWidth !== acc.width || frameHeight !== acc.height || codes.length < acc.width * acc.height) return false
  if (frame.group < 0 || frame.group >= acc.groups) return false
  const { factor, cw, ch, n, mt, mv, stt, svv, stv } = acc
  const t = frame.t
  const base = frame.group * cw * ch
  const half = factor >> 1
  const cap = n instanceof Uint8Array ? 255 : MAX_PER_GROUP
  for (let cy = 0; cy < ch; cy++) {
    const y = Math.min(acc.height - 1, cy * factor + half)
    const row = y * acc.width
    let k = base + cy * cw
    for (let cx = 0; cx < cw; cx++, k++) {
      const v = values[codes[row + Math.min(acc.width - 1, cx * factor + half)]]
      if (!Number.isFinite(v) || n[k] >= cap) continue
      const n1 = n[k] + 1
      const dt = t - mt[k]
      const dv = v - mv[k]
      const mtN = mt[k] + dt / n1
      const mvN = mv[k] + dv / n1
      stt[k] += dt * (t - mtN)
      svv[k] += dv * (v - mvN)
      stv[k] += dt * (v - mvN)
      mt[k] = mtN
      mv[k] = mvN
      n[k] = n1
    }
  }
  acc.framesPerGroup[frame.group]++
  acc.yearsPerGroup[frame.group].add(frame.year)
  return true
}

/**
 * The standard deviation of v about its least-squares line in t, from the
 * centred moments (Σ(t−t̄)², Σ(v−v̄)², Σ(t−t̄)(v−v̄)): the residual variance
 * on n − 2 degrees of freedom. With no spread in t, the plain sample σ on
 * n − 1. Null below two values (three with a trend).
 */
export function residualSigma(n: number, stt: number, svv: number, stv: number): number | null {
  if (!(n >= 2)) return null
  if (n >= 3 && stt > 1e-9) return Math.sqrt(Math.max(0, svv - (stv * stv) / stt) / (n - 2))
  return Math.sqrt(Math.max(0, svv) / (n - 1))
}

/**
 * The same from raw sums (n, Σt, Σt², Σv, Σv², Σtv), for a series held in
 * double precision (tests, and published examples).
 */
export function detrendedSigma(n: number, st: number, stt: number, sy: number, syy: number, sty: number): number | null {
  if (!(n >= 2)) return null
  return residualSigma(n, stt - (st * st) / n, syy - (sy * sy) / n, sty - (st * sy) / n)
}

// ---------------------------------------------------------------- the data's own codes

/**
 * A value table read as a continuous scale: the data codes (contiguous,
 * finite, monotonic) between `first` and `last`, so a mean can be written
 * as a fractional code and read back by interpolating the table, and a
 * step is the gap between two neighbouring codes.
 */
export interface CodeScale {
  readonly values: Float64Array
  readonly first: number
  readonly last: number
}

/** The data codes of a value table; null when they are not one contiguous, monotonic run. */
export function codeScale(values: ArrayLike<number>): CodeScale | null {
  let first = -1
  let last = -1
  for (let c = 0; c < values.length; c++) {
    if (!Number.isFinite(values[c])) continue
    if (first < 0) first = c
    else if (c !== last + 1) return null
    last = c
  }
  if (first < 0 || last <= first) return null
  const up = values[last] >= values[first]
  for (let c = first; c < last; c++) if (up ? values[c + 1] < values[c] : values[c + 1] > values[c]) return null
  return { values: Float64Array.from(values as ArrayLike<number>), first, last }
}

/** The fractional code whose interpolated value is v, clamped to the data codes. */
export function fracCode(s: CodeScale, v: number): number {
  const vals = s.values
  const up = vals[s.last] >= vals[s.first]
  const below = (a: number) => (up ? a <= v : a >= v)
  if (!below(vals[s.first])) return s.first
  if (below(vals[s.last])) return s.last
  let lo = s.first
  let hi = s.last
  while (hi - lo > 1) {
    const mid = (lo + hi) >> 1
    if (below(vals[mid])) lo = mid
    else hi = mid
  }
  const span = vals[hi] - vals[lo]
  return span === 0 ? lo : lo + (v - vals[lo]) / span
}

/** The value at a fractional code: v[c0] + (v[c0 + 1] − v[c0]) × f, as the shader computes it. */
export function valueAtFrac(s: CodeScale, cf: number): number {
  const c0 = Math.min(s.last, Math.max(s.first, Math.floor(cf)))
  const f = cf - c0
  const v0 = s.values[c0]
  if (!(f > 0) || c0 >= s.last) return v0
  return v0 + (s.values[c0 + 1] - v0) * f
}

/** One quantisation step at a fractional code: the gap between the two codes around it. */
export function stepAtFrac(s: CodeScale, cf: number): number {
  const seg = Math.min(s.last - 1, Math.max(s.first, Math.floor(cf)))
  return Math.abs(s.values[seg + 1] - s.values[seg])
}

// ---------------------------------------------------------------- the finished climatology

export interface Climatology {
  readonly calendar: ClimateCalendar
  readonly groups: number
  readonly width: number
  readonly height: number
  readonly factor: number
  readonly cw: number
  readonly ch: number
  /**
   * Per cell per group, interleaved [mean, σ], layer (group) by layer,
   * row-major: the mean as a fractional code × 256 ({@link NO_MEAN}: none),
   * σ as a multiple of the step at the mean × 256 (0: none; at least 256,
   * one step).
   */
  readonly q: Uint16Array
  /** The data's codes the means are written in. */
  readonly scale: CodeScale
  /** Whether any cell has a σ. */
  readonly hasSigma: boolean
  /** Cells (over all groups) with a mean, with a σ, and whose σ was raised to one step. */
  readonly cellsWithMean: number
  readonly cellsWithSigma: number
  readonly flooredCells: number
  readonly framesPerGroup: readonly number[]
  readonly yearsPerGroup: readonly number[]
  /** Frames per group, skipped ones too (the 80 % rule's denominator). */
  readonly expectedPerGroup: readonly number[]
}

export interface FinishOptions {
  /** Estimate σ (not for a window). */
  readonly sigma: boolean
  /** The data's codes (`codeScale` of the value table). */
  readonly scale: CodeScale
  readonly calendar: ClimateCalendar
  /** Frames per group the 80 % rule counts against (defaults to those read). */
  readonly expectedPerGroup?: readonly number[]
  readonly minCoverage?: number
  readonly minSigmaYears?: number
}

/**
 * Turn the running sums into the climatology: each cell's mean and σ, as
 * 16-bit codes. Works in the sums' own arrays, so no second copy of the
 * grid is made.
 */
export function finishClimatology(acc: ClimatologyAccumulator, opts: FinishOptions): Climatology {
  const cells = acc.cw * acc.ch
  const size = cells * acc.groups
  const minCoverage = opts.minCoverage ?? MIN_COVERAGE
  const minYears = opts.minSigmaYears ?? MIN_SIGMA_YEARS
  const s = opts.scale
  const q = new Uint16Array(size * 2)
  let cellsWithMean = 0
  let cellsWithSigma = 0
  let flooredCells = 0
  for (let g = 0; g < acc.groups; g++) {
    const frames = opts.expectedPerGroup?.[g] ?? acc.framesPerGroup[g]
    const need = frames ? Math.max(1, Math.ceil(minCoverage * frames - 1e-9)) : Infinity
    const sigmaHere = opts.sigma && acc.yearsPerGroup[g].size >= minYears
    for (let c = 0; c < cells; c++) {
      const k = g * cells + c
      const n = acc.n[k]
      if (!(n >= need) || !acc.framesPerGroup[g]) {
        q[k * 2] = NO_MEAN
        continue
      }
      const cf = fracCode(s, acc.mv[k])
      q[k * 2] = Math.min(NO_MEAN - 1, Math.max(0, Math.round(cf * 256)))
      cellsWithMean++
      if (!sigmaHere || n < minYears) continue
      const sd = residualSigma(n, acc.stt[k], acc.svv[k], acc.stv[k])
      if (sd === null) continue
      // The step at the mean as decoded, so the floor and the decode agree.
      const step = stepAtFrac(s, q[k * 2] / 256)
      if (!(step > 0)) continue
      let units = sd / step
      if (units < 1) {
        units = 1
        flooredCells++
      }
      q[k * 2 + 1] = Math.min(65535, Math.max(256, Math.round(units * 256)))
      cellsWithSigma++
    }
  }
  return {
    calendar: opts.calendar,
    groups: acc.groups,
    width: acc.width,
    height: acc.height,
    factor: acc.factor,
    cw: acc.cw,
    ch: acc.ch,
    q,
    scale: s,
    hasSigma: cellsWithSigma > 0,
    cellsWithMean,
    cellsWithSigma,
    flooredCells,
    framesPerGroup: [...acc.framesPerGroup],
    yearsPerGroup: acc.yearsPerGroup.map((y) => y.size),
    expectedPerGroup: opts.expectedPerGroup ? [...opts.expectedPerGroup] : [...acc.framesPerGroup],
  }
}

/** The cell (within one group's layer) that a texel of the dataset's grid reads. */
export function cellOf(clim: Pick<Climatology, 'factor' | 'cw' | 'ch'>, x: number, y: number): number {
  const cx = Math.min(clim.cw - 1, Math.max(0, Math.floor(x / clim.factor)))
  const cy = Math.min(clim.ch - 1, Math.max(0, Math.floor(y / clim.factor)))
  return cy * clim.cw + cx
}

/** A mean code's value (interpolating the data's table); NaN for none. */
export function decodeMean(clim: Pick<Climatology, 'scale'>, code: number): number {
  return code === NO_MEAN ? NaN : valueAtFrac(clim.scale, code / 256)
}

/** A σ code's value, in the step at its cell's mean; NaN for none. */
export function decodeSigma(clim: Pick<Climatology, 'scale'>, meanCode: number, code: number): number {
  return code === 0 || meanCode === NO_MEAN ? NaN : (code / 256) * stepAtFrac(clim.scale, meanCode / 256)
}

/** The mean and σ at a texel of the dataset's grid, for one group; NaN where there are none. */
export function climatologyAt(clim: Climatology, x: number, y: number, group: number): { mean: number; sigma: number } {
  if (group < 0 || group >= clim.groups) return { mean: NaN, sigma: NaN }
  const k = (group * clim.cw * clim.ch + cellOf(clim, x, y)) * 2
  return { mean: decodeMean(clim, clim.q[k]), sigma: decodeSigma(clim, clim.q[k], clim.q[k + 1]) }
}

/**
 * A value read against the climatology, by mode: the change in units, the
 * % change of the mean, or the standardised change (σ). 'grey' where there
 * is data on one side only, a mean too near zero for a percentage, or no
 * σ; null where neither side has data. The paused picture, the readers and
 * the graphics card (`earthTileLayer.ts`, `uClimOn`) follow these rules.
 */
export function periodChange(now: number, mean: number, sigma: number, mode: ChangeMode, floor: number | null): number | 'grey' | null {
  const a = Number.isFinite(now)
  const b = Number.isFinite(mean)
  if (!a && !b) return null
  if (a !== b) return 'grey'
  const diff = now - mean
  if (mode === 'percent') {
    if (Math.abs(mean) < (floor ?? 0) || mean === 0) return 'grey'
    return (100 * diff) / Math.abs(mean)
  }
  if (mode === 'sigma') return Number.isFinite(sigma) && sigma > 0 ? diff / sigma : 'grey'
  return diff
}

/** The standardised change: (value − mean) / σ; null without a σ. */
export function standardisedChange(value: number, mean: number, sigma: number): number | null {
  if (!Number.isFinite(value) || !Number.isFinite(mean) || !(sigma > 0)) return null
  return (value - mean) / sigma
}

/** The |mean| magnitudes over every group, sampled (every 7th cell) for the % floor. */
export function meanMagnitudes(clim: Climatology): number[] {
  const out: number[] = []
  const size = clim.cw * clim.ch * clim.groups
  for (let k = 0; k < size; k += 7) {
    const m = decodeMean(clim, clim.q[k * 2])
    if (Number.isFinite(m)) out.push(Math.abs(m))
  }
  return out
}

/** One row of the paired latitude profile: the frame's and the climatology's mean over the cells both have. */
export interface AnomalyRow {
  readonly lat: number
  readonly now: number | null
  readonly clim: number | null
  readonly count: number
}

/**
 * The frame against the climatology over a region, place by place and
 * only where both have data: the area-weighted mean of (value − mean of
 * its month), the mean σ change and the share of that area beyond ±2σ,
 * and a latitude profile of both on the same cells. Comparing a frame's
 * extremes or percentiles with a field of means would mean little, so
 * those are not compared. With a thinned grid each place reads the frame
 * at its cell's texel.
 */
export interface AnomalySummary {
  readonly meanChange: number
  /** Area-weighted mean of (value − mean) / σ over the cells with a σ; null without one. */
  readonly meanSigma: number | null
  /** Share (0–1) of the area with a σ where |z| > 2; null without one. */
  readonly shareBeyond2: number | null
  readonly areaKm2: number
  readonly cells: number
  readonly rows: readonly AnomalyRow[]
}

export function anomalySummary(
  now: LumaSnapshot,
  nowValues: ArrayLike<number>,
  clim: Climatology,
  group: number,
  options: DatasetOverlayOptions | undefined,
  window?: TexelWindow,
): AnomalySummary | null {
  if (now.width !== clim.width || now.height !== clim.height || group < 0 || group >= clim.groups) return null
  const x0 = Math.max(0, Math.floor(window?.x0 ?? 0))
  const y0 = Math.max(0, Math.floor(window?.y0 ?? 0))
  const x1 = Math.min(now.width, Math.ceil(window?.x1 ?? now.width))
  const y1 = Math.min(now.height, Math.ceil(window?.y1 ?? now.height))
  const areas = rowAreasKm2(now.width, now.height, options)
  const layer = group * clim.cw * clim.ch
  let w = 0
  let sumDiff = 0
  let wz = 0
  let sumZ = 0
  let beyond = 0
  let cells = 0
  const rows: AnomalyRow[] = []
  for (let y = y0; y < y1; y++) {
    let rn = 0
    let rc = 0
    let count = 0
    for (let x = x0; x < x1; x++) {
      // AYNI: a region's shape inside its rectangle (a country).
      if (window?.mask && !inWindowMask(window, x, y)) continue
      const at = sampledTexel(clim, x, y)
      const a = nowValues[now.data[at.y * now.width + at.x]]
      const k = (layer + cellOf(clim, x, y)) * 2
      const m = decodeMean(clim, clim.q[k])
      if (!Number.isFinite(a) || !Number.isFinite(m)) continue
      count++
      rn += a
      rc += m
      const area = areas[y]
      w += area
      sumDiff += area * (a - m)
      const sd = decodeSigma(clim, clim.q[k], clim.q[k + 1])
      if (Number.isFinite(sd) && sd > 0) {
        const z = (a - m) / sd
        wz += area
        sumZ += area * z
        if (Math.abs(z) > 2) beyond += area
      }
    }
    cells += count
    rows.push({
      lat: texelUvToLatLon({ u: 0.5, v: (y + 0.5) / now.height }, options).lat,
      now: count ? rn / count : null,
      clim: count ? rc / count : null,
      count,
    })
  }
  if (!(w > 0)) return null
  return {
    meanChange: sumDiff / w,
    meanSigma: wz > 0 ? sumZ / wz : null,
    shareBeyond2: wz > 0 ? beyond / wz : null,
    areaKm2: w,
    cells,
    rows,
  }
}
